import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { environment } from '../../environments/environment';
import 'rxjs/add/observable/interval';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/first';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';

import { localStorageAuthToken, localStorageAuthUser, localStorageCartId, localStorageToken } from '../_common/constants';
import { IAuthLogin } from '../_models/auth-login';
import { IAuthRefresh } from '../_models/auth-refresh';
import { IAuthState } from '../_models/auth-state';
import { IAuthToken } from '../_models/auth-token';
import { IAuthUser } from '../_models/auth-user';
import { CartsService } from './carts.service';

// import * as JwtDecode from 'jwt-decode';

@Injectable()
export class LoginService {
    private readonly apiController: string;
    private refreshSubscription: Subscription;
    private readonly authStateSubject: BehaviorSubject<IAuthState>;

    authState: Observable<IAuthState>;
    authToken: Observable<IAuthToken>;
    authUser: Observable<IAuthUser>;

    constructor(private readonly httpClient: HttpClient, private readonly cartsService: CartsService) {
        this.apiController = `${environment.webApiUrl}/api/v${environment.webApiVersion}/login`;
        this.authStateSubject = new BehaviorSubject<IAuthState>({authReady: false, authToken: null});
        this.authState = this.authStateSubject.asObservable();
        this.authToken = this.authStateSubject.filter(authState => authState.authReady).map((authState: IAuthState) => authState.authToken as IAuthToken);
    }

    getLoggedInUser(): IAuthUser {
        return this.retrieveStoredAuthUser();
    }

    isLoggedIn(): boolean {
        // TODO: Need to handle token expiration check here!
        return !!localStorage.getItem(localStorageToken);
    }

    login(authLogin: IAuthLogin) {
        return this.getToken(authLogin, 'password').map(() => this.scheduleTokenRefresh());
    }

    logout(): void {
        // TODO: Remove tokens and call API to logout.
        this.updateAuthState({authReady: false, authToken: null});
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
        this.clearAuthLoginTokenAndCart();
    }

    refreshToken(): Observable<IAuthToken> {
        return this.authState.first().map((authState: IAuthState) =>
            authState.authToken).mergeMap((authToken: IAuthToken) =>
            this.getToken({refreshToken: authToken.refreshToken}, 'refreshToken')
                .catch(() => Observable.throw('Session Expired')));
    }

    storeLoggedInUser(authUser: IAuthUser): void {
        this.storeAuthUser(authUser);
    }

    private clearAuthLoginTokenAndCart(): void {
        localStorage.removeItem(localStorageAuthToken);
        localStorage.removeItem(localStorageAuthUser);
        localStorage.removeItem(localStorageToken);
        this.cartsService.clear(localStorage.getItem(localStorageCartId));
    }

    private getToken(data: IAuthLogin | IAuthRefresh, grantType: string): Observable<IAuthUser> {
        const httpHeaders = new HttpHeaders({'Content-Type': 'application/json'});

        Object.assign(data, {grantType: grantType, scope: 'openid profile offline_access'});

        return this.httpClient.post<any>(`${this.apiController}/token`, data, {headers: httpHeaders})
            .map(
                (authUser: IAuthUser) => {
                    if (authUser && authUser.token) {
                        this.storeAuthUser(authUser);
                        localStorage.setItem(localStorageToken, authUser.token);
                        const now = new Date();
                        const authToken = {
                            accessToken: authUser.token,
                            refreshToken: authUser.refreshToken,
                            expirationDate: new Date(now.getTime() + (authUser.expiresIn * 10000)).getTime().toString(),
                            expiresIn: authUser.expiresIn
                        };
                        this.updateAuthState({authReady: true, authToken: authToken});
                    }

                    return authUser;
                }
            );
    }

    private retrieveStoredAuthToken(): IAuthToken {
        const authTokenString = localStorage.getItem(localStorageAuthToken);
        return authTokenString == null ? null : JSON.parse(authTokenString);
    }

    private retrieveStoredAuthUser(): IAuthUser {
        const storedAuthUserAsString = localStorage.getItem(localStorageAuthUser);
        return storedAuthUserAsString == null ? null : JSON.parse(storedAuthUserAsString);
    }

    private scheduleTokenRefresh(): void {
        this.refreshSubscription = this.authToken
            .first()
            .mergeMap((authToken: IAuthToken) => Observable
                .interval((authToken.expiresIn / 2) * 60000)
            )
            .mergeMap(() => this.refreshToken())
            .subscribe();
    }

    private storeAuthUser(authUser: IAuthUser): void {
        const previousAuthUser = this.retrieveStoredAuthUser();
        if (previousAuthUser != null && authUser.refreshToken == null) {
            authUser.refreshToken = previousAuthUser.refreshToken;
        }
        localStorage.setItem(localStorageAuthUser, JSON.stringify(authUser));
    }

    private updateAuthState(newAuthState: IAuthState) {
        const previousAuthState = this.authStateSubject.getValue();
        this.authStateSubject.next(Object.assign({}, previousAuthState, newAuthState));
    }
}



// WEBPACK FOOTER //
// ./src/app/_services/login.service.ts